import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { X } from "lucide-react";
import { useAlert } from "../MessageAlert/AlertContext";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/store";
import { FormControl } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import {LOCALSTORAGE_KEYS,DEFAULT_UI_DICTIONARY } from '..//../utils/Constants';
import {getStorageItem, SetStorageItem} from '../../helpers/helpers'
import { getAccessToken as getManagedAccessToken } from '../../services/tokenManager';
import { useCappedOptions, LoadAllFooter } from './dropdownOptions';

const Dropdown = React.memo(({
	label,
	value,
	options,
	onChange,
	isMulti,
	required,
	disabled = false,
}: any) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");
	
	const dropdownRef = useRef<HTMLDivElement>(null); 

	// Filtering, the render cap and the "Load all" escape hatch are shared with
	// EmailLog.tsx and LogTimeslipForm.tsx - see ./dropdownOptions. This list
	// used to render every match, which is what froze the pane on the ~50k
	// matter list; only the first MAX_VISIBLE_OPTIONS reach the DOM now.
	const {
		filteredOptions,
		visibleOptions,
		hiddenCount,
		showAllOptions,
		isExpanding,
		loadAll,
	} = useCappedOptions(options, search, isOpen);

	const handleRemove = (optionToRemove: any) => {
	  const newValue = value.filter(
		(item: any) => item.value !== optionToRemove.value
	  );
	  onChange(newValue);
	};
	
	useEffect(() => {
		const handleOutsideClick = (event: MouseEvent) => {
		  if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		  ) {
			setIsOpen(false);
		  }
		};

		document.addEventListener("mousedown", handleOutsideClick);
		return () => {
		  document.removeEventListener("mousedown", handleOutsideClick);
		};
	  }, []);
	return (
	  <div className="" ref={dropdownRef}>
		<label className="block">
		  {label}
		  {required && <span className="text-red-500">*</span>}
		</label>
		<div className="relative">
		  <div
			className={`border rounded-lg p-2 min-h-[10px] ${
			  disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-text bg-white'
			}`}
			onClick={() => !disabled && setIsOpen(true)}
		  >
			{isMulti ? (
			  <div className="flex flex-wrap gap-1">
				{value?.map((v: any) => (
				  <span
					key={v?.value}
					className="inline-flex items-center bg-blue-50 rounded-md px-2 m-1"
				  >
					<span className={`text-sm ${disabled ? 'text-gray-600' : 'text-gray-700'}`}>{v?.label}</span>
					{!disabled && (
					  <button
						onClick={(e) => {
						  e.stopPropagation();
						  handleRemove(v);
						}}
						className="ml-2 text-gray-400 hover:text-gray-600"
					  >
						<X size={14} />
					  </button>
					)}
				  </span>
				))}
				{!disabled ? (
				  <input
					type="text"
					className="outline-none flex-grow min-w-[100px] text-sm p-1"
					placeholder="Type to search..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onFocus={() => setIsOpen(true)}
				  />
				) : (
				  // Only show placeholder when no tags are selected and disabled
				  value?.length === 0 && (
					<span className="text-sm text-gray-500">Select an option...</span>
				  )
				)}
			  </div>
			) : (
			  <div className="flex items-center">
				{value ? (
				  <>
					<span className={`text-sm ${disabled ? 'text-gray-600' : 'text-gray-700'}`}>{value.label}</span>
					{!disabled && (
					  <button
						onClick={(e) => {
						  e.stopPropagation();
						  onChange(null);
						}}
						className="ml-2 text-gray-400 hover:text-gray-600"
					  >
						<X size={14} />
					  </button>
					)}
				  </>
				) : (
				  <>
					{!disabled ? (
					  <input
						type="text"
						className="outline-none w-full text-sm"
						placeholder="Type to search..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onFocus={() => setIsOpen(true)}
					  />
					) : (
					  <span className="text-sm text-gray-500">Select an option...</span>
					)}
				  </>
				)}
			  </div>
			)}
		  </div>
  
		  {isOpen && !disabled && (
			<div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
			  {visibleOptions.map((option) => (
				<div
				  key={option.value}
				  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
				  onClick={() => {
					if (isMulti) {
					  if (!value.find((item: any) => item.value === option.value)) {
						onChange([...value, option]);
					  }
					} else {
					  onChange(option);
					}
					setSearch("");
					setIsOpen(false);
				  }}
				>
				  {option.label}
				</div>
			  ))}
			  <LoadAllFooter
				filteredCount={filteredOptions.length}
				hiddenCount={hiddenCount}
				showAllOptions={showAllOptions}
				isExpanding={isExpanding}
				onLoadAll={loadAll}
			  />
			  {filteredOptions.length === 0 && (
				<div className="px-4 py-2 text-xs text-gray-500">No matches</div>
			  )}
			</div>
		  )}
		  {!disabled && (
			<ChevronDown
			  className={`absolute right-2 top-3 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
			  size={16}
			/>
		  )}
		</div>
	  </div>
	);
  });
Dropdown.displayName = "Dropdown";

const LogClarraDocsForm = ({setCommunicationSave,setOnlyDocs,ResetFormData,data,handleSubmitNewClarraDocs, files, setErrors,
	errors,
	expanded,
	formData,
	setFormData,}) => {
	const { showAlert } = useAlert();
	const SavedData = data;
	const dispatch = useDispatch<AppDispatch>();
	const { uiDictionary } = useSelector((state: RootState) => state.labels);
	const matters = useSelector((state: RootState) => state.matters.matters);
	const mattersOptions = matters.map((matter) => ({
		value: matter.id,
		label: matter.matter + " (" + matter.number + ")",
	}));
	const fileArray = files.map((item) => ({
		filename: item.filename
	  }));
	const ServiceType = useSelector(
		(state: RootState) => state.serviceType.serviceType
	);
	const serviceTypeOptions = ServiceType.map((service) => ({
		label: service.name, 
		value: service.id,    
	  }));
	const tags = useSelector((state: RootState) => state.tags.tags);
	const [AccessToken, SetAccessToken] = useState(null);

	const [selectedMatter, setSelectedMatter] = useState(null);
	const [selectedTags, setSelectedTags] = useState([]);
	const {getAccessTokenSilently} = useAuth0();
	const tagsOptions = tags.map((tags) => ({
		label: tags.name, 
		value: tags.id,     
	  }));

	  const formatDate = (date) => {
		
		
		const Emaildate =  getStorageItem(LOCALSTORAGE_KEYS.ItemDate);
		if(Emaildate !=null) {
		  const date1 = new Date(Emaildate); 
		  const year = date1.getFullYear();
		  const month = String(date1.getMonth() + 1).padStart(2, "0"); // Months are zero-based
		  const day = String(date1.getDate()).padStart(2, "0");
		  return `${year}-${month}-${day}`;
		}
		else {
		  const year = date.getFullYear();
		  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
		  const day = String(date.getDate()).padStart(2, "0");
		  return `${year}-${month}-${day}`;
		}
	};
	
	  const validateForm = () => {
		const newErrors: any = {};
		// if (!formData.matter) newErrors.matter = "Matter is required.";
		// if (!formData.date) newErrors.date = "Date is required.";
		// if (!formData.status) newErrors.status = "Status is required.";
		// if (!formData.serviceType) newErrors.serviceType = "Service Type is required.";
		// if (formData.tags.length === 0)newErrors.tags = "At least one tag is required.";
		// if (!formData.description) newErrors.description = "Description is required.";
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	  };
	const onCancel = ()=>{
		setCommunicationSave(false);
		setOnlyDocs(false);
	}
	
	const handleSubmit = (e) => {
		e.preventDefault();
		if (validateForm()) {
		setCommunicationSave(false);
		formData.Data = AccessToken;
		formData.attachments = files;
		handleSubmitNewClarraDocs(formData);
		setOnlyDocs(false);
		ResetFormData();
		}
	};
	

	const removeFile = (index) => {
		const newFiles = [...formData.attachmentsdoc];
		const removed = newFiles.splice(index, 1)[0];
		setFormData({
			...formData,
			attachmentsdoc: newFiles
		});

		// Remove from Outlook mail item if in compose mode and Office.js is available
		if (
			window.Office &&
			Office.context.mailbox.item &&
			typeof Office.context.mailbox.item.removeAttachmentAsync === "function"
		) {
			Office.context.mailbox.item.getAttachmentsAsync((result) => {
				if (result.status === Office.AsyncResultStatus.Succeeded) {
					const attachments = result.value || [];
					const match = attachments.find(a => a.name === removed.filename);
					if (match) {
						Office.context.mailbox.item.removeAttachmentAsync(match.id, (removeResult) => {
							if (removeResult.status !== Office.AsyncResultStatus.Succeeded) {
								console.error("Failed to remove attachment from mail:", removeResult.error);
							}
						});
					}
				}
			});
		}
	};
	
	useEffect(() => {
		(async () => {
			const x = getAccessToken();
		})();
	}, [dispatch, AccessToken]);
	useEffect(() => {
		const findMatter = (mattersOptions !=null) ? mattersOptions.find(item => item.label == formData.matterdoc) : null;
        	setSelectedMatter(findMatter);	
	}, [formData.matterdoc]);
	const getAccessToken = async () => {
		let token;
		try {
			// tokenManager is the single source of truth for the live Access
			// Token (in-memory; refreshed via the Public API session handle on
			// the classic-Outlook path, or via the registered silent-refresh on
			// the modern-host path - see src/services/tokenManager.ts). This
			// used to round-trip the raw token through localStorage; removed -
			// no business logic depends on that (the only consumer of this
			// component's AccessToken state, ClarraDocsData.Token in
			// BaseComponent.tsx's handleSubmitNewClarraDocs, is commented out).
			token = await getManagedAccessToken();
			if (token == null) {
				token = await getAccessTokenSilently();
			}
			if (token != null) {
				SetAccessToken(token);
			}
		} catch (exception) {

		}
		return token;
	};
	
	useEffect(() => {
    // Only run if in readonly mode, matterdoc is a fallback number, and we know what number to look for
    if (
        formData.isClarraDocsReadOnly &&
        formData.clarraDocsMatterNumber && // comes from step 1!
        mattersOptions.length > 0
    ) {
        // Find nice label (e.g. "Acme Corp (1000172)")
        const found = mattersOptions.find(opt => {
            const match = opt.label.match(/\((\d+)\)$/);
            return match && match[1] === String(formData.clarraDocsMatterNumber);
        });
        // If found, and not already set, update the label in formData
        if (found && found.label !== formData.matterdoc) {
            setFormData(prev => ({
                ...prev,
                matterdoc: found.label,
				matteriddoc : found.value
            }));
        }
    }
}, [formData.isClarraDocsReadOnly, formData.clarraDocsMatterNumber, mattersOptions, setFormData]);

	// Add useEffect to sync selectedTags when formData changes
	useEffect(() => {
		if (formData.isClarraDocsReadOnly && formData.tagdoc && Array.isArray(formData.tagdoc)) {
			setSelectedTags(formData.tagdoc);
		}
	}, [formData.isClarraDocsReadOnly, formData.tagdoc]);

	useEffect(() => {
		if (formData.matterdoc == "") {
			setSelectedMatter(null);
		}
		if (formData.tagdoc.length == 0) {
			setSelectedTags([]);
		}
	}, [formData]);
	

	// Update handleMatterChange to prevent changes when in read-only mode
	const handleMatterChange = (value: any) => {
		// Prevent matter changes when in read-only mode
		if (formData.isClarraDocsReadOnly) {
			return;
		}
		
		setSelectedMatter(value);
		SetStorageItem(LOCALSTORAGE_KEYS.SelectedMatterDoc, (value != null) ? value.label : null);
		setFormData((prev: any) => ({
			...prev,
			matterdoc: (value != null) ? value.label : null,
			matteriddoc : (value != null) ? value.value : 0,
			matterdoc_manually_set: true,
		}));
	};

	// Update handleTagsChange to prevent changes when in read-only mode
	const handleTagsChange = (value: any[]) => {
		// Prevent tag changes when in read-only mode
		if (formData.isClarraDocsReadOnly) {
			return;
		}
		
		setSelectedTags(value);
		const selTags = (value != null && value.length > 0) ?
			value.map((item) => item.label).join(", ") : "";
		SetStorageItem(LOCALSTORAGE_KEYS.SelectedTagsDoc, selTags);
		setFormData((prev: any) => ({
			...prev,
			tagdoc: value,
		}));
	};
	return (
		<div className="bg-white w-full">
			
			<form onSubmit={handleSubmit} className="space-y-4">
				{/* Matter and Date Row */}
				<FormControl  fullWidth sx={{marginTop:'10px'}}>
						<Dropdown
                  label={uiDictionary.Matter}
				  value={selectedMatter}
                   options={mattersOptions}
				   onChange={handleMatterChange}
                    isMulti={false}
                    required
                    disabled={formData.isClarraDocsReadOnly}
                />	
				          {expanded.includes('panel3') && formData.createClarraDocs &&!formData.matterdoc && errors.matterdoc && <p className="text-red-500 text-xs">{errors.matterdoc}</p>}
						  </FormControl>
					<div>
						<label className="block text-sm text-gray-600 mb-1">
							Date <span className="text-red-500">*</span>
						</label>
						{formData.isClarraDocsReadOnly ? (
							<input
								type="text"
								value={formData.datedoc || ''}
								disabled={true}
								className="w-full p-1 border rounded bg-gray-100 cursor-not-allowed text-gray-600"
							/>
							) : (
							<input
								type="date"
								value={formData.datedoc}
								onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									datedoc: e.target.value,
								}))
								}
								className="w-full p-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								disabled={false}
							/>
						)}
					</div>
				{/* Status and Service Type Row */}
				<FormControl  fullWidth sx={{marginTop:'10px'}}>
					<Dropdown
              label={uiDictionary.Status}
			 value={formData.status}
			 options={[	{label:"Received"}, {label:"Sent"}]}
				   onChange={(value) => {
						if (formData.isClarraDocsReadOnly) return;
						setFormData({ ...formData, status: value });
					}}
                    isMulti={false}
                    required
                    disabled={formData.isClarraDocsReadOnly}
                />	
				          {expanded.includes('panel3') && formData.createClarraDocs&&!formData.status && errors.status && <p className="text-red-500 text-xs">{errors.status}</p>}
						  </FormControl>
						  <FormControl  fullWidth >
					<Dropdown
            label={uiDictionary.MailTypes}
			   options={serviceTypeOptions}
				   value={formData.serviceType}
				   onChange={(value) => {
						if (formData.isClarraDocsReadOnly) return;
						setFormData({ ...formData, serviceType: value });
					}}
                    isMulti={false}
                    required
                    disabled={formData.isClarraDocsReadOnly}
                />	
				          {expanded.includes('panel3') && formData.createClarraDocs&&!formData.serviceType && errors.serviceType && <p className="text-red-500 text-xs">{errors.serviceType}</p>}
				</FormControl>
				{/* Tags */}
				<div className="relative">
					<div className="relative">
						<FormControl  fullWidth sx={{marginTop:'5px'}}>
					<Dropdown
          label={uiDictionary.Tags}
		  value={selectedTags}
		  options={tagsOptions}
		  onChange={handleTagsChange}
                    isMulti={true}
                    required
                    disabled={formData.isClarraDocsReadOnly}
                />
				          {expanded.includes('panel3') && formData.createClarraDocs&&(formData?.tagdoc?.length === 0) && errors.tagdoc && <p className="text-red-500 text-xs">{errors.tagdoc}</p>}
				</FormControl>
					</div>
				</div>

				{/* Description */}
				<div>
					<label className="block text-sm text-gray-600 mb-1">
						{uiDictionary.Description} <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						value={formData.descriptiondoc}
						onChange={(e) => {
							if (formData.isClarraDocsReadOnly) return;
							setFormData({
								...formData,
								descriptiondoc: e.target.value,
							});
						}}
						className="w-full p-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						disabled={formData.isClarraDocsReadOnly}
					/>
				 {expanded.includes('panel3') && formData.createClarraDocs&&(formData?.descriptiondoc) && errors.descriptiondoc && <p className="text-red-500 text-xs">{errors.descriptiondoc}</p>}
			
				</div>

				{/* Note */}
				<div>
					<label className="block text-sm text-gray-600 mb-1">
						{uiDictionary.Note}
					</label>
					<textarea
						value={formData.notedoc}
						onChange={(e) => {
							if (formData.isClarraDocsReadOnly) return;
							setFormData({ ...formData, notedoc: e.target.value });
						}}
						placeholder={`Enter your ${uiDictionary.Note} here`}
						rows={4}
						className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						disabled={formData.isClarraDocsReadOnly}
					/>
				</div>

				{/* Attachments */}
				<div>
					<label className="block text-sm text-gray-600 mb-1">
						{uiDictionary.Attachment} <span className="text-red-500">*</span>
					</label>
						<div
							key="-1"
							className="flex items-center justify-between p-2 border-b"
						 >	
							<span className="text-sm text-gray-600">
								{formData.emlFileName}
							</span>
							
						</div>
					{formData.attachmentsdoc.map((file, index) => (
						<div
							key={index}
							className="flex items-center justify-between p-2 border-b"
						>	
							<span className="text-sm text-gray-600">
								 {file.filename} 
								
							</span>
							{formData.attachmentsdoc.length > 0 && !formData.isClarraDocsLogged &&<button
									type="button"
									onClick={() => removeFile(index)}
									className="text-gray-400 hover:text-gray-600"
								>
									<X size={16} />
								</button> 
							}
							{/* } */}
						</div>

					))}
				</div>
				{/* Dotted line separator */}
				<div className="border-t border-dotted my-6"></div>

				{/* Docketing Section */}
				<div className="space-y-4">
					<label className="flex items-center space-x-2">
						<input
						type="checkbox"
						checked={formData.sendToDocketing}
						onChange={(e) =>
							setFormData({
								...formData,
								sendToDocketing: e.target.checked,
							})
						}
							className="form-checkbox h-4 w-4 text-blue-600"
							disabled={formData.isClarraDocsReadOnly}
						/>
						<span className="text-sm text-gray-600">
							Send to {uiDictionary.Docketing}
						</span>
					</label>

					<label className="flex items-center space-x-2">
						<input
						type="checkbox"
						checked={formData.docketingComplete}
						onChange={(e) =>
							setFormData({
								...formData,
								docketingComplete: e.target.checked,
							})
						}
							className="form-checkbox h-4 w-4 text-blue-600"
							disabled={formData.isClarraDocsReadOnly}
						/>
						<span className="text-sm text-gray-600">
							{uiDictionary.Docketing} Complete
						</span>
					</label>

					<div>
						<label className="block text-sm text-gray-600 mb-1">
							{uiDictionary.Docketing} Notes
						</label>
						<textarea
						value={formData.docketingNotes}
						onChange={(e) => {
							if (formData.isClarraDocsReadOnly) return;
							setFormData({
								...formData,
								docketingNotes: e.target.value,
							});
						}}
							rows={3}
							className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							disabled={formData.isClarraDocsReadOnly}
						/>
					</div>
				</div>
			</form>
		</div>
	);
};

export default LogClarraDocsForm;
